import { segment } from '@actions/analytics';
import { submitDoubt } from '@api/fetchers/doubts-and-solutions';
import useSubjectAndLanguages from '@api/hooks/goal/custom-doubts/useSubjectAndLanguages';
import { DOW2_DOUBT_SUBMITTED } from '@constants/segment';
import { Dispatch, SetStateAction, useCallback, useContext } from 'react';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { DoubtsAIContext } from '@context/DoubtsAIContext';
import { subjectSelectionState } from '../DoubtSolution/constants';

const doubtSubEventProps = {
  doubt_type: 'AAD',
  feature_version: 2,
  flow_type: 'Non_Demo',
  doubt_subject_selection: subjectSelectionState.defaultSubjectSelection
};

const useDoubtSubmission = ({
  goalUID,
  signed_url,
  public_url,
  isASDEnabled,
  setUploadedImageUrl,
  setPolling
}: {
  goalUID: string;
  isASDEnabled: boolean;
  signed_url: string | undefined;
  public_url: string | undefined;
  setUploadedImageUrl: Dispatch<SetStateAction<string>>;
  setPolling: () => void;
}) => {
  const { setDoubtsUID, setSubmittedImageUrl } = useContext(DoubtsAIContext);

  const { subjectAndLanguageData: submitDoubtDetails } =
    useSubjectAndLanguages(goalUID);
  const routeGoalSegmentData = useGoalEventProps({ goalUID });

  const handleDoubtSubmission = useCallback(
    async (onSuccess: () => void) => {
      // if asd enabled submit doubt
      if (isASDEnabled && signed_url) {
        setSubmittedImageUrl(public_url);
        const res1 = await submitDoubt(goalUID, {
          ...submitDoubtDetails,
          uploadedImageUrl: public_url,
          isASDEnabled,
          isAiDoubtsFlow: true
        });
        if (res1.success) {
          const doubtUID = res1.uid;
          setDoubtsUID(doubtUID);
          setUploadedImageUrl('');
          setPolling();
          onSuccess();
        } else {
          segment.track(DOW2_DOUBT_SUBMITTED, {
            doubt_upload_status: 'Failed',
            doubtUID: res1?.uid,
            ...doubtSubEventProps,
            ...routeGoalSegmentData
          });
        }
      }
    },
    [
      goalUID,
      isASDEnabled,
      public_url,
      routeGoalSegmentData,
      setDoubtsUID,
      setPolling,
      setSubmittedImageUrl,
      setUploadedImageUrl,
      signed_url,
      submitDoubtDetails
    ]
  );

  const handleRemovePreview = useCallback(() => {
    setSubmittedImageUrl(null);
    setUploadedImageUrl('');
    setDoubtsUID(undefined);
  }, [setDoubtsUID, setSubmittedImageUrl, setUploadedImageUrl]);

  return {
    handleDoubtSubmission,
    handleRemovePreview
  };
};

export default useDoubtSubmission;
